import { omit } from 'lodash';

import {
  AdditionalReference,
  Declaration,
  DeclarationDocumentsEnum,
  IAdditionalCodesRiskAnalysis,
  RecursivePartial,
  TransactionTypes,
  TransportEquipment,
} from '@e-origin/shared';

import { DECLARATION_CUSTOMS_PROCEDURE_LENGTH, DECLARATION_CUSTOMS_PROCEDURE_SUBSTRING_SEPARATOR } from '@constants';

export interface CommodityCodeRow {
  riskAnalysis: IAdditionalCodesRiskAnalysis;
  firstItemData: {
    hsCode: string;
    nationalAdditionalCode: string;
    nationalAdditionalCode2: string;
    nationalAdditionalCode3: string;
    taricAdditionalCode: string;
    taricAdditionalCode2: string;
  };
  additionalReference?: {
    selected: boolean;
    document: AdditionalReference;
  }[];
}

export interface BeforeSendingFormData extends RecursivePartial<Declaration> {
  customer: any;
  warehouse: string;
  customsProcedureCurrent: string;
  customsProcedurePrevious: string;
  commodityCodes: CommodityCodeRow[];
  declarationGoods: any;
}

const buildValuationInformationTaxes = (declaration: Declaration) => {
  const valuationInformationTaxes: RecursivePartial<Declaration['valuationInformationTaxes']> = {
    chargesimport:
      declaration.transactionType !== TransactionTypes.B2B_MULTIPLE
        ? {
            CustomsCharges: {
              charges: declaration.globalInfo.valuationInformationTaxes?.chargesimport?.CustomsCharges?.charges || 0,
              currency: declaration.globalInfo.valuationInformationTaxes?.chargesimport?.CustomsCharges?.currency,
            },
            VATcharges: {
              charges: declaration.globalInfo.valuationInformationTaxes?.chargesimport?.VATcharges?.charges || 0,
              currency: declaration.globalInfo.valuationInformationTaxes?.chargesimport?.VATcharges?.currency,
            },
          }
        : undefined,
    valuationAdjustment: {
      valuationIndicators: declaration.valuationInformationTaxes?.valuationAdjustment?.valuationIndicators,
    },
    valuationMethod: declaration.valuationInformationTaxes?.valuationMethod,
  };

  if ([TransactionTypes.B2B_HV, TransactionTypes.B2B_EXPORT, TransactionTypes.B2B2C_HV, undefined].includes(declaration.transactionType)) {
    valuationInformationTaxes.deliveryTerms = {
      incotermCode: declaration.valuationInformationTaxes?.deliveryTerms?.incotermCode,
      location: declaration.valuationInformationTaxes?.deliveryTerms?.location,
      country: declaration.valuationInformationTaxes?.deliveryTerms?.country,
    };
  }

  return valuationInformationTaxes;
};

const buildContainers = (transportEquipment: TransportEquipment) => {
  const containers = transportEquipment?.containers?.length
    ? transportEquipment.containers.map((container) => ({
        identificationNumber: container.identificationNumber,
        seals: container.seals || [],
      }))
    : [
        {
          identificationNumber: '',
          seals: [],
        },
      ];

  if (transportEquipment?.seals?.length) {
    containers[0].seals.push(...transportEquipment.seals);
  }

  return containers;
};

export const beforeSendingBatchFormIntializer = (declaration: Declaration): BeforeSendingFormData => {
  const isB2BTransaction = [TransactionTypes.B2B_HV, TransactionTypes.B2B_EXPORT, TransactionTypes.B2B2C_HV, undefined].includes(declaration.transactionType);
  return {
    customer: declaration.customer,
    warehouse: declaration.documents?.warehouseDocuments?.[0]?.identifier || '',
    customsProcedureCurrent: declaration?.goodShipmentGlobal?.customsProcedure?.substring(0, DECLARATION_CUSTOMS_PROCEDURE_SUBSTRING_SEPARATOR),
    customsProcedurePrevious: declaration?.goodShipmentGlobal?.customsProcedure?.substring(
      DECLARATION_CUSTOMS_PROCEDURE_SUBSTRING_SEPARATOR,
      DECLARATION_CUSTOMS_PROCEDURE_LENGTH,
    ),
    goodShipmentGlobal: {
      procedureHeader: declaration.goodShipmentGlobal.procedureHeader ?? true,
      customsProcedureType: declaration.goodShipmentGlobal.customsProcedureType,
      customsNationalProcedure: declaration.goodShipmentGlobal.customsNationalProcedure,
      customsProcedure: declaration.goodShipmentGlobal.customsProcedure,
      customsNationalProcedureCode: declaration.goodShipmentGlobal.customsNationalProcedureCode,
      preference: declaration.goodShipmentGlobal.preference,
    },
    generalInfo: {
      group: declaration.generalInfo.group,
    },
    messageInfo: {
      declarationMainType: declaration.messageInfo.declarationMainType,
      declarationType: declaration.messageInfo.declarationType,
      methodOfPayment: declaration.messageInfo.methodOfPayment,
      deferredPayment: declaration.messageInfo.deferredPayment,
      methodOfPaymentVAT: declaration.messageInfo.methodOfPaymentVAT,
      deferredPaymentVAT: declaration.messageInfo.deferredPaymentVAT,
      security: declaration.messageInfo.security,
      presentationNotificationEstimatedDateTime: declaration.messageInfo.presentationNotificationEstimatedDateTime,
    },
    consignment: {
      carrier: declaration.consignment.carrier,
      carrierHeader: declaration.consignment.carrierHeader,
      ...(isB2BTransaction
        ? {
            countryOfDispatchHeader: declaration.consignment.countryOfDispatchHeader,
            countryOfDispatch: declaration.consignment.countryOfDispatch,
            countryOfExportHeader: declaration.consignment.countryOfExportHeader,
            countryOfExport: declaration.consignment.countryOfExport,
            destinationHeader: declaration.consignment.destinationHeader,
            countryOfDestination: declaration.consignment.countryOfDestination,
            regionOfDestination: declaration.consignment.regionOfDestination,
          }
        : {}),
      locationOfGoods: {
        unLocode: declaration.consignment.locationOfGoods.unLocode,
      },
      customsOfficeExit: {
        referenceNumber: declaration.consignment.customsOfficeExit?.referenceNumber,
      },
      customsSupervisingOffice: {
        referenceNumber: declaration.consignment.customsSupervisingOffice?.referenceNumber,
      },
      customsOfficeExport: {
        referenceNumber: declaration.consignment.customsOfficeExport?.referenceNumber,
      },
      customsOfficePresentation: {
        referenceNumber: declaration.consignment.customsOfficePresentation?.referenceNumber,
      },
    },
    transportInformation: {
      inlandModeOfTransport: declaration.transportInformation?.inlandModeOfTransport,
      modeOfTransportMeansAtTheBorder: declaration.transportInformation?.modeOfTransportMeansAtTheBorder,
      arrivalTransportMeans: {
        identificationNumber: declaration.transportInformation?.arrivalTransportMeans?.identificationNumber,
        typeOfIdentification: declaration.transportInformation?.arrivalTransportMeans?.typeOfIdentification,
      },
      departureTransportMeans: {
        identificationNumber: declaration.transportInformation?.departureTransportMeans?.identificationNumber,
        typeOfIdentification: declaration.transportInformation?.departureTransportMeans?.typeOfIdentification,
        nationality: declaration.transportInformation?.departureTransportMeans?.nationality,
      },
      activeBorderTransportMeans: {
        identificationNumber: declaration.transportInformation?.activeBorderTransportMeans?.identificationNumber,
        typeOfIdentification: declaration.transportInformation?.activeBorderTransportMeans?.typeOfIdentification,
        nationality: declaration.transportInformation?.activeBorderTransportMeans?.nationality,
      },
      transportEquipment: {
        header: declaration.transportInformation?.transportEquipment?.header,
        containerIdentification: declaration.transportInformation?.transportEquipment?.containerIdentification,
        containers: buildContainers(declaration.transportInformation?.transportEquipment),
      },
      countryOfRoutingOfConsignment: declaration.transportInformation?.countryOfRoutingOfConsignment?.length
        ? declaration.transportInformation?.countryOfRoutingOfConsignment
        : [{ country: '' }],
    },
    valuationInformationTaxes: buildValuationInformationTaxes(declaration),
    documents: (() => {
      const documents = omit(
        declaration.documents,
        declaration.transactionType === TransactionTypes.B2C_HV_BEGATE ? [DeclarationDocumentsEnum.PREVIOUS_DOCUMENTS] : [],
      );
      return Object.keys(documents).reduce((acc, docType) => {
        return {
          ...acc,
          [docType]: (documents[docType] || [])
            .map((doc) => ({
              ...doc,
              header: doc.header === undefined ? true : doc.header,
            }))
            .filter((doc) => doc.header),
        };
      }, {});
    })(),
    declarationGoods: [TransactionTypes.B2B_HV, TransactionTypes.B2B_EXPORT, TransactionTypes.B2B2C_HV, undefined].includes(declaration.transactionType)
      ? {
          packaging: {
            typeOfPackages: declaration.goodsShipmentItems[0].packaging.typeOfPackages,
          },
          additionalProcedure: declaration.goodsShipmentItems[0].additionalProcedure || [],
        }
      : {
          additionalProcedure: declaration.goodsShipmentItems[0].additionalProcedure || [],
        },
    commodityCodes: declaration.globalInfo.goodShipmentGlobals.map((goodShipmentGlobal) => ({
      riskAnalysis: {
        hsCodeFound: (goodShipmentGlobal as any).hsCodeFound,
        taricData: {},
      },
      firstItemData: {
        hsCode: goodShipmentGlobal.firstItemData.hsCode,
        nationalAdditionalCode: goodShipmentGlobal.firstItemData.nationalAdditionalCode,
        nationalAdditionalCode2: goodShipmentGlobal.firstItemData.nationalAdditionalCode2,
        nationalAdditionalCode3: goodShipmentGlobal.firstItemData.nationalAdditionalCode3,
        taricAdditionalCode: goodShipmentGlobal.firstItemData.taricAdditionalCode,
        taricAdditionalCode2: goodShipmentGlobal.firstItemData.taricAdditionalCode2,
      },
      supportingDocuments: [],
    })),
    customsState: {
      bcpMrn: declaration.customsState?.bcpMrn,
    },
  };
};
